import { useCallback, useEffect, useState } from 'react';
import type { AdminUserView, InviteView } from '@pgp/shared';
import { net } from '../net';

/**
 * Admin panel, shown in the lobby only to admins.
 *
 * Invites are links rather than bare codes: a link is something you can paste
 * into a chat and the recipient can just tap, and the sign-in screen reads the
 * code out of it.
 */
/** Built here rather than server-side: this page knows the origin people
 *  actually use, without trusting any proxy header. */
function inviteUrl(code: string): string {
  return `${window.location.origin}/?invite=${encodeURIComponent(code)}`;
}

export function Admin() {
  const [invites, setInvites] = useState<InviteView[]>([]);
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/state?token=${encodeURIComponent(net.token ?? '')}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not load');
      setInvites(json.invites as InviteView[]);
      setUsers(json.users as AdminUserView[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load');
    }
  }, []);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const post = async (route: string, body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(route, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: net.token, ...body }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Something went wrong');
      await refresh();
      return json;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code: string) => {
    const url = inviteUrl(code);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard access needs a secure context and can still be refused; the
      // link stays selectable either way.
      setError('Could not copy automatically — select the link and copy it.');
      return;
    }
    setCopied(code);
    window.setTimeout(() => setCopied(null), 2000);
  };

  const unused = invites.filter((i) => !i.usedBy);
  const used = invites.filter((i) => i.usedBy);

  if (!open) {
    return (
      <section>
        <h2>Admin</h2>
        <button onClick={() => setOpen(true)}>Open admin panel</button>
      </section>
    );
  }

  return (
    <section className="admin">
      <h2>
        Admin
        <button className="link" onClick={() => setOpen(false)}>Hide</button>
      </h2>

      {error && <div className="banner error">{error}</div>}

      <div className="admin-actions">
        <button
          className="primary"
          disabled={busy}
          onClick={() => void post('/api/admin/invite', { days: 14 })}
        >
          Create invite link
        </button>
        <button disabled={busy} onClick={() => void refresh()}>Refresh</button>
      </div>

      <h3 className="admin-head">Unused invites ({unused.length})</h3>
      {unused.length === 0 && <p className="hint">None. Create one above.</p>}
      <ul className="invites">
        {unused.map((i) => (
          <li key={i.code}>
            <div className="invite-main">
              <code className="invite-url">{inviteUrl(i.code)}</code>
              <span className="hint">
                {i.expiresAt ? `expires ${new Date(i.expiresAt).toLocaleDateString()}` : 'never expires'}
              </span>
            </div>
            <div className="invite-buttons">
              <button onClick={() => void copy(i.code)}>
                {copied === i.code ? 'Copied' : 'Copy'}
              </button>
              <button
                className="link danger"
                disabled={busy}
                onClick={() => void post('/api/admin/invite/revoke', { code: i.code })}
              >
                Revoke
              </button>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="admin-head">People ({users.length})</h3>
      <ul className="admin-users">
        {users.map((u) => (
          <li key={u.id}>
            <span className="pname">
              {u.name} <span className="hint">@{u.username}</span>
            </span>
            {u.isAdmin && <span className="admin-tag">admin</span>}
            <button
              disabled={busy || (u.isAdmin && u.id === net.state.user?.id)}
              onClick={() => void post('/api/admin/set-admin', { userId: u.id, isAdmin: !u.isAdmin })}
            >
              {u.isAdmin ? 'Remove admin' : 'Make admin'}
            </button>
          </li>
        ))}
      </ul>

      {used.length > 0 && (
        <>
          <h3 className="admin-head">Used invites ({used.length})</h3>
          <ul className="invites">
            {used.map((i) => (
              <li key={i.code} className="is-used">
                <div className="invite-main">
                  <code>{i.code}</code>
                  <span className="hint">used by {i.usedBy}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
